/**
 * R202 — Category × City programmatic SEO landing.
 * URL: /business-ideas/<category>-franchise-in-<city>
 * Rendered by BusinessIdeasOneSeg when seg1 contains "-franchise-in-".
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/venture/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startRecommendationFlow } from "@/lib/cta";
import PartnersSection from "@/components/partners/PartnersSection";
import FranchiseMatchSection from "@/components/venture/FranchiseMatchSection";
import RiskNudge from "@/components/venture/RiskNudge";
import {
  ChevronRight, MapPin, Wallet, TrendingUp, Clock, Building2, ArrowRight,
  ShieldCheck, Users, Landmark, Sparkles,
} from "lucide-react";

const api = axios.create({ baseURL: API_BASE });
const SITE = typeof window !== "undefined" ? window.location.origin : "https://venturekhoj.com";

function fmtPop(n) {
  if (!n) return null;
  n = Number(n);
  if (n >= 1e7) return `${(n / 1e7).toFixed(1)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
}

export default function CategoryCityLanding() {
  const { seg1 } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { category, city } = useMemo(() => {
    const s = (seg1 || "").toLowerCase();
    const i = s.indexOf("-franchise-in-");
    if (i < 0) return { category: null, city: null };
    return { category: s.slice(0, i), city: s.slice(i + "-franchise-in-".length) };
  }, [seg1]);

  useEffect(() => {
    if (!category || !city) { setNotFound(true); setLoading(false); return; }
    let on = true;
    setLoading(true); setNotFound(false);
    api.get(`/seo/category-city`, { params: { category, city } })
      .then((r) => { if (on) { setData(r.data); setLoading(false); } })
      .catch(() => { if (on) { setNotFound(true); setLoading(false); } });
    window.scrollTo(0, 0);
    return () => { on = false; };
  }, [category, city]);

  const startLead = () => {
    startRecommendationFlow({
      user, nav: navigate,
      prefill: { category_slug: category, city_slug: city, source: "category_city_seo" },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 space-y-4">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-12 w-full max-w-2xl" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="catcity-notfound">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Page not found</h1>
          <p className="mt-2 text-slate-500">We couldn't find that franchise-in-city combination.</p>
          <Link to="/business-ideas" className="mt-6 inline-block">
            <Button className="rounded-full">Browse business ideas <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </Link>
        </div>
      </div>
    );
  }

  const { category: cat, city: ct, seo, brand_count, fallback_brands, nav } = data;
  const canonical = `${SITE}${seo.canonical_path}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${cat.name} Franchise in ${ct.name}`,
    serviceType: `${cat.name} Franchise`,
    areaServed: { "@type": "City", name: ct.name, ...(ct.state ? { address: { "@type": "PostalAddress", addressRegion: ct.state, addressCountry: "IN" } } : {}) },
    description: seo.description,
    provider: { "@type": "Organization", name: "VentureKhoj", url: SITE },
    offers: {
      "@type": "Offer", priceCurrency: "INR",
      ...(cat.min_capital ? { price: String(Math.round(Number(cat.min_capital))) } : {}),
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50/60 to-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8 sm:py-12">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5 mb-5" data-testid="catcity-breadcrumbs">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/business-ideas" className="hover:text-slate-900">Business Ideas</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/business-ideas/location/${ct.slug}`} className="hover:text-slate-900">{ct.name}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 font-medium">{cat.name} Franchise</span>
          </nav>

          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold mb-3">
            <Sparkles className="h-3.5 w-3.5" /> 2026 opportunity · {ct.state || "India"}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight max-w-4xl" data-testid="catcity-h1">
            {seo.h1}
          </h1>
          <p className="mt-4 text-slate-600 max-w-3xl text-base sm:text-lg" data-testid="catcity-intro">
            {seo.description}
          </p>

          {/* Stat chips */}
          <div className="mt-6 flex flex-wrap gap-3">
            <Stat icon={Wallet} label="Investment" value={cat.investment_band} tone="emerald" />
            {cat.typical_margin_pct ? <Stat icon={TrendingUp} label="Typical margin" value={`${cat.typical_margin_pct}%`} tone="indigo" /> : null}
            {cat.typical_roi_months ? <Stat icon={Clock} label="Payback" value={`${cat.typical_roi_months} mo`} tone="violet" /> : null}
            {ct.population ? <Stat icon={Users} label="Population" value={fmtPop(ct.population)} tone="slate" /> : null}
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={startLead} className="w-full sm:w-auto rounded-2xl h-auto min-h-12 py-3 px-6 text-sm font-semibold whitespace-normal text-left items-start justify-start leading-snug" data-testid="catcity-lead-cta">
              <span className="flex-1">Looking to start a {cat.name} franchise in {ct.name}? Check If It Really Fits You</span>
              <ArrowRight className="h-4 w-4 ml-2 mt-0.5 shrink-0" />
            </Button>
          </div>
          <div className="mt-3">
            <RiskNudge
              testId="catcity-risk-note"
              text="Before you invest money, time & effort — check feasibility first. A small step vs the cost of a wrong pick."
            />
          </div>
        </div>
      </section>

      {/* Brand feed — gated verified brands grouped by category, else national fallback */}
      {brand_count > 0 ? (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10" data-testid="catcity-brands">
          <FranchiseMatchSection
            businessIdeaId={cat.slug}
            businessIdeaName={`${cat.name} in ${ct.name}`}
            targetCity={ct.name}
            limit={24}
          />
        </section>
      ) : (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10" data-testid="catcity-fallback-brands">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
              Top franchise brands expanding across India
            </h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            No {cat.name} brand is live in {ct.name} yet — here are verified brands actively onboarding partners nationwide.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fallback_brands.map((b) => {
              const band = [b.minimum_investment, b.maximum_investment].filter(Boolean)
                .map((n) => `₹${Math.round(Number(n) / 100000)}L`).join(" – ");
              return (
                <Link key={b.slug} to={`/franchise/${b.slug}`} data-testid={`catcity-fb-${b.slug}`}
                  className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 hover:border-navy hover:shadow-lg transition-all">
                  <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-white border border-slate-200 grid place-items-center p-1.5">
                    {b.logo_url ? <img src={b.logo_url} alt={`${b.brand_name} logo`} loading="lazy" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      : <Building2 className="h-6 w-6 text-navy/70" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-slate-900 text-[15px] truncate group-hover:text-navy">{b.brand_name}</div>
                    <div className="mt-1 text-[12px] text-slate-600 font-medium flex flex-wrap gap-x-3">
                      {band ? <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5 text-emerald-600" />{band}</span> : null}
                      {b.payback_months ? <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-emerald-600" />{b.payback_months} mo</span> : null}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-6">
            <Button onClick={startLead} className="rounded-full h-11 px-6 text-sm font-semibold" data-testid="catcity-fallback-cta">
              Get matched with brands for {ct.name} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </section>
      )}

      {/* City opportunity context */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-10">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-emerald-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">Why {ct.name} for a {cat.name} franchise</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-slate-700">
            <p>
              {ct.name}{ct.state ? `, ${ct.state}` : ""} is a {ct.tier ? `tier-${ct.tier} ` : ""}market
              {ct.population ? ` of ~${fmtPop(ct.population)} people` : ""}, making it a strong catchment for a {cat.name} outlet.
              Expected investment is {cat.investment_band}
              {cat.typical_margin_pct ? ` with typical margins near ${cat.typical_margin_pct}%` : ""}
              {cat.typical_roi_months ? ` and payback around ${cat.typical_roi_months} months` : ""}.
            </p>
            <div className="space-y-2">
              {ct.average_shop_rent_display ? (
                <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-slate-400" /><span>Avg. shop rent: <b>{ct.average_shop_rent_display}</b></span></div>
              ) : null}
              {Array.isArray(ct.major_industries) && ct.major_industries.length > 0 ? (
                <div className="flex items-start gap-2"><TrendingUp className="h-4 w-4 text-slate-400 mt-0.5" /><span>Key industries: {ct.major_industries.slice(0, 5).join(", ")}</span></div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Internal link grids */}
      <section className="border-t border-slate-100 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 grid lg:grid-cols-2 gap-8">
          <LinkGrid
            title={`${cat.name} franchise in other cities`}
            items={nav.nearby_cities}
            testid="catcity-nav-cities"
          />
          <LinkGrid
            title={`Other franchises in ${ct.name}`}
            items={nav.sibling_categories}
            testid="catcity-nav-categories"
          />
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    slate: "bg-white border-slate-200 text-slate-700",
  };
  return (
    <div className={"rounded-xl border px-4 py-2.5 " + (tones[tone] || tones.slate)}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-0.5 font-heading text-lg font-black leading-none">{value}</div>
    </div>
  );
}

function LinkGrid({ title, items, testid }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <h3 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it) => (
          <Link key={it.path} to={it.path}
            className="text-[13px] text-slate-600 hover:text-navy hover:underline truncate py-1"
            data-testid={`${testid}-link`}>
            {it.name} <ArrowRight className="inline h-3 w-3 opacity-50" />
          </Link>
        ))}
      </div>
    </div>
  );
}
